// TikTok-style Reels video cache.
//
// Prefetches short Reel videos into memory as Blob object-URLs so that:
//   • swiping to the NEXT reel plays instantly (bytes already downloaded), and
//   • swiping BACK to a reel replays from memory with NO re-download.
//
// Keyed by the resolved media URL → the infinite-scroll loop (which repeats the same
// reels with fresh React keys) reuses ONE blob per unique reel, so memory stays bounded
// by the number of unique reels near the viewport, not by how far the user scrolls.
//
// Everything is best-effort: if the CDN blocks cross-origin fetch (CORS) or the network
// fails, prefetch resolves to null and the caller falls back to the plain <video src>.
// Evicted blobs are revoked; a <video> that still points at an evicted blob must handle
// onError by falling back to the direct URL (see Reels.jsx) — so eviction never breaks.

let MAX_ENTRIES = 10;                   // ~ unique reels kept in memory at once (admin-tunable)
const MAX_BYTES = 60 * 1024 * 1024;     // don't cache a single video larger than this

// Admin-tunable memory budget: how many decoded reels to keep at once. Larger = smoother
// re-scroll but more RAM; smaller = leaner for members on weak devices/networks.
export function setReelCacheLimit(n) {
  const v = Math.max(2, Math.min(Number(n) || 10, 30));
  MAX_ENTRIES = v;
  evictIfNeeded();
}

const cache = new Map();     // url -> blobUrl (insertion order = LRU)
const inflight = new Map();  // url -> Promise<blobUrl|null>

function touch(url) {
  // Re-insert to move to the most-recently-used end.
  if (cache.has(url)) {
    const v = cache.get(url);
    cache.delete(url);
    cache.set(url, v);
  }
}

function evictIfNeeded() {
  while (cache.size > MAX_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    const blobUrl = cache.get(oldestKey);
    cache.delete(oldestKey);
    try { URL.revokeObjectURL(blobUrl); } catch { /* noop */ }
  }
}

export function getReelBlob(url) {
  if (!url) return null;
  if (cache.has(url)) { touch(url); return cache.get(url); }
  return null;
}

export function prefetchReel(url) {
  if (!url) return Promise.resolve(null);
  if (cache.has(url)) { touch(url); return Promise.resolve(cache.get(url)); }
  if (inflight.has(url)) return inflight.get(url);
  const p = (async () => {
    try {
      const res = await fetch(url, { credentials: "omit", mode: "cors" });
      if (!res.ok) return null;
      const len = Number(res.headers.get("content-length") || 0);
      if (len && len > MAX_BYTES) return null;   // too big → stream directly instead
      const blob = await res.blob();
      if (blob.size > MAX_BYTES) return null;
      const blobUrl = URL.createObjectURL(blob);
      cache.set(url, blobUrl);
      evictIfNeeded();
      return blobUrl;
    } catch {
      return null;   // CORS / network → caller uses the plain URL
    } finally {
      inflight.delete(url);
    }
  })();
  inflight.set(url, p);
  return p;
}

// Drop a specific dead blob (called from onError so a later prefetch can retry cleanly).
export function dropReelBlob(url) {
  if (url && cache.has(url)) {
    const blobUrl = cache.get(url);
    cache.delete(url);
    try { URL.revokeObjectURL(blobUrl); } catch { /* noop */ }
  }
}

// Vide tout le cache (app native : en quittant la page Reels, pour ne pas garder jusqu'à 4 clips en RAM sous le Fil).
export function releaseReelCache() {
  for (const blobUrl of cache.values()) { try { URL.revokeObjectURL(blobUrl); } catch { /* noop */ } }
  cache.clear();
}

export const reelCacheSize = () => cache.size;
